/**
 * Near-Me Enums
 * =============
 * Const-object enums (mirrors the marketplace-schema idiom). Each `as const`
 * object doubles as the source for `z.enum(...)` and for Mongoose `enum:`.
 */

/** Lifecycle of a business listing. `removed` is moderation-only. */
export const NEAR_ME_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  PAUSED: 'paused',
  CLOSED: 'closed',
  REMOVED: 'removed',
} as const;
export type NearMeStatus = (typeof NEAR_ME_STATUS)[keyof typeof NEAR_ME_STATUS];

/**
 * Managed business attributes / amenities. This is the curated tag vocabulary
 * (NOT free-text) that owners pick from and seekers filter on.
 */
export const NEAR_ME_TAG = {
  HOME_DELIVERY: 'home_delivery',
  TAKEAWAY: 'takeaway',
  DINE_IN: 'dine_in',
  ONLINE_BOOKING: 'online_booking',
  APPOINTMENT_ONLY: 'appointment_only',
  WALK_IN_WELCOME: 'walk_in_welcome',
  OPEN_24_HOURS: 'open_24_hours',
  EMERGENCY_SERVICE: 'emergency_service',
  FREE_WIFI: 'free_wifi',
  PARKING_AVAILABLE: 'parking_available',
  WHEELCHAIR_ACCESSIBLE: 'wheelchair_accessible',
  AIR_CONDITIONED: 'air_conditioned',
  CARD_PAYMENT: 'card_payment',
  UPI_PAYMENT: 'upi_payment',
  CASH_ONLY: 'cash_only',
  HOME_SERVICE_AVAILABLE: 'home_service_available',
  FREE_CONSULTATION: 'free_consultation',
  WOMEN_ONLY: 'women_only',
  PET_FRIENDLY: 'pet_friendly',
  FAMILY_FRIENDLY: 'family_friendly',
} as const;
export type NearMeTag = (typeof NEAR_ME_TAG)[keyof typeof NEAR_ME_TAG];

/** Reasons a seeker can report a listing. Feeds the shared moderation queue. */
export const NEAR_ME_REPORT_REASON = {
  FAKE_OR_SCAM: 'fake_or_scam',
  WRONG_INFORMATION: 'wrong_information',
  PERMANENTLY_CLOSED: 'permanently_closed',
  INAPPROPRIATE_CONTENT: 'inappropriate_content',
  SPAM: 'spam',
  DUPLICATE: 'duplicate',
  OFFENSIVE: 'offensive',
  OTHER: 'other',
} as const;
export type NearMeReportReason = (typeof NEAR_ME_REPORT_REASON)[keyof typeof NEAR_ME_REPORT_REASON];

/** How a lead reached the owner. Revenue-relevant (metered per plan later). */
export const NEAR_ME_LEAD_TYPE = {
  CALL: 'call',
  WHATSAPP: 'whatsapp',
  ENQUIRY: 'enquiry',
  QUOTE: 'quote',
  CALLBACK: 'callback',
  APPOINTMENT: 'appointment',
} as const;
export type NearMeLeadType = (typeof NEAR_ME_LEAD_TYPE)[keyof typeof NEAR_ME_LEAD_TYPE];

/** Lifecycle of a lead in the owner's inbox. */
export const NEAR_ME_LEAD_STATUS = {
  NEW: 'new',
  VIEWED: 'viewed',
  CONTACTED: 'contacted',
  CONVERTED: 'converted',
  CLOSED: 'closed',
  SPAM: 'spam',
} as const;
export type NearMeLeadStatus = (typeof NEAR_ME_LEAD_STATUS)[keyof typeof NEAR_ME_LEAD_STATUS];

/** Days of week for opening hours (lowercase, Mongoose-enum friendly). */
export const NEAR_ME_DAY = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday',
} as const;
export type NearMeDay = (typeof NEAR_ME_DAY)[keyof typeof NEAR_ME_DAY];
